/**
 * Cart Protection — Redo "Free Returns + Package Protection" in the cart.
 *
 * Redo (app embed + checkout UI extension) sells one coverage product,
 * vendor "re:do", with ~80 price-tier variants. Its checkout extension adds a
 * tier automatically ("auto-check") and does NOT remove it when the customer
 * empties the cart, so the theme is left with an orphaned line that the
 * drawer hides but the subtotal and header badge still count.
 *
 * This module gives the customer the control instead — an on/off switch in
 * the cart drawer and on the cart page — and does exactly what Redo's own
 * Hydrogen SDK (@redotech/redo-hydrogen) does, so pricing, tier selection and
 * per-country / per-currency rules stay Redo's:
 *
 *   1. Quote:  POST https://api.getredo.com/v2.2/stores/{storeId}/coverage-products
 *              with the cart lines, total and customer country. Public, no
 *              auth. The response carries the fee Redo intends
 *              (coverageProducts[0].price) and a variantId.
 *              The fee, not the id, decides which tier goes in the cart: the
 *              theme picks the first live tier at or above the fee from the
 *              product's own variants — exactly what Redo's checkout
 *              extension does. Redo's variantId comes from a price table
 *              that lags Shopify by a revision, so using it put a higher
 *              tier in the cart which checkout then replaced with a lower
 *              one in front of the customer. The id is kept only as a
 *              fallback when the product JSON is unavailable. The theme
 *              never computes a fee.
 *   2. ON:     one /cart/update.js call adds that variant and sets the cart
 *              attribute `redo_opted_in_from_cart` = "true" (the signal the
 *              checkout extension reads — verified: it then keeps our line and
 *              adds nothing else).
 *   3. OFF:    one /cart/update.js call removes every "re:do" line and sets
 *              the attribute to "false" (verified: checkout then adds nothing
 *              and renders its box unchecked). The choice is remembered in
 *              localStorage for `optoutDays` so it survives the session.
 *   4. Empty:  when no real product remains, every "re:do" line is removed.
 *              That is housekeeping, not a customer choice, and never writes
 *              the remembered choice.
 *
 * Protection lines are identified by vendor "re:do" — the SDK's own test —
 * with a title fallback. No variant or product id list: Redo adds tiers at
 * every price change and the old id list had drifted from 58 to 80 variants.
 *
 * Settings (Theme settings > Shipping Protection): enable_shipping_protection
 * (feature switch), shipping_protection_default (on for a new visitor),
 * shipping_protection_optout_days, shipping_protection_redo_store_id.
 * layout/theme.liquid emits them as window.theme.settings.shippingProtection,
 * ungated, and loads this file unconditionally so the empty-cart cleanup
 * runs even when the switch itself is turned off.
 *
 * Deliberately NOT inside assets/collection-offer.js: that file is only
 * loaded while a Collection Offer is enabled.
 *
 * Everything above the "Runtime" line is pure and exposed on
 * window.theme.cartProtection so tests/cart-protection.test.js can load this
 * file in a vm sandbox with no DOM.
 */

(function () {
  'use strict';

  const root = typeof window !== 'undefined' ? window : globalThis;
  const theme = (root.theme = root.theme || {});
  theme.settings = theme.settings || {};

  const CP = (theme.cartProtection = theme.cartProtection || {});

  CP.VENDOR = 're:do';
  CP.ATTRIBUTE = 'redo_opted_in_from_cart';
  CP.API_HOST = 'https://api.getredo.com/v2.2';
  CP.STORAGE_KEY = 'shippingProtectionChoice';
  CP.TITLE_PATTERNS = ['free returns', 'package protection', 'shipping protection'];

  // ---------------------------------------------------------------------------
  // Pure — no DOM below this line until "Runtime".
  // ---------------------------------------------------------------------------

  /**
   * Is this /cart.js line Redo's coverage product?
   * Vendor is the contract (the SDK filters on it); the title match is a
   * belt-and-braces fallback for a renamed vendor.
   */
  CP.isProtectionLine = function isProtectionLine(item) {
    if (!item) return false;
    if (String(item.vendor || '').toLowerCase() === CP.VENDOR) return true;
    const title = String(item.product_title || item.title || '').toLowerCase();
    return !!title && CP.TITLE_PATTERNS.some((p) => title.indexOf(p) !== -1);
  };

  CP.protectionLines = function protectionLines(cart) {
    return ((cart && cart.items) || []).filter((item) => CP.isProtectionLine(item));
  };

  /**
   * Units in the cart that are not protection. Gift cards count — a gift
   * card on its own is a real order and keeps its protection.
   */
  CP.realItemCount = function realItemCount(cart) {
    return ((cart && cart.items) || []).reduce((sum, item) => (
      CP.isProtectionLine(item) ? sum : sum + (Number(item.quantity) || 0)
    ), 0);
  };

  /**
   * The remembered choice: true (opted in), false (opted out) or null
   * (nothing remembered / expired). Stored as {choice, expiry}. The old
   * shipping-protection.js shape {choice, timestamp} is treated as expired
   * and cleared.
   */
  CP.readChoice = function readChoice(storage, now) {
    let raw = null;
    try { raw = storage && storage.getItem(CP.STORAGE_KEY); } catch (e) { return null; }
    if (!raw) return null;
    let data = null;
    try { data = JSON.parse(raw); } catch (e) { data = null; }
    const ts = now || Date.now();
    if (!data || typeof data.choice !== 'boolean' || !(Number(data.expiry) > ts)) {
      try { storage.removeItem(CP.STORAGE_KEY); } catch (e) { /* ignore */ }
      return null;
    }
    return data.choice;
  };

  CP.writeChoice = function writeChoice(storage, choice, days, now) {
    const ts = now || Date.now();
    const ms = Math.max(1, Number(days) || 7) * 24 * 60 * 60 * 1000;
    const data = { choice: !!choice, expiry: ts + ms };
    try { storage.setItem(CP.STORAGE_KEY, JSON.stringify(data)); } catch (e) { /* ignore */ }
    return data;
  };

  /**
   * Should protection be in the cart?
   *   - feature off, or no real items        → no
   *   - customer chose (remembered)          → their choice
   *   - otherwise                            → the merchant default
   */
  CP.desired = function desired(opts) {
    const o = opts || {};
    if (!o.enabled) return false;
    if (!(Number(o.realCount) > 0)) return false;
    if (o.choice === true) return true;
    if (o.choice === false) return false;
    return !!o.defaultOn;
  };

  /**
   * Cart signature the quote depends on: real lines, quantities, currency.
   * Protection lines are excluded so adding one does not trigger a re-quote.
   */
  CP.quoteSignature = function quoteSignature(cart) {
    const items = ((cart && cart.items) || []).filter((i) => !CP.isProtectionLine(i));
    const cur = (cart && cart.currency) || '';
    return cur + '|' + items.map((i) => i.variant_id + ':' + i.quantity + ':' + i.final_line_price).join(',');
  };

  /**
   * Body for POST /stores/{storeId}/coverage-products, shaped exactly as the
   * SDK sends it (including customer.country, which drives Redo's per-country
   * pricing and is not in their published schema). Protection lines are left
   * out, as the SDK does, so the quote is for the real goods only.
   */
  CP.quoteRequest = function quoteRequest(cart, ctx) {
    const c = ctx || {};
    const currency = (cart && cart.currency) || c.currency || '';
    const money = (cents) => ({ amount: (Number(cents || 0) / 100).toFixed(2), currency });
    const items = ((cart && cart.items) || []).filter((i) => !CP.isProtectionLine(i));
    const lineItems = items.map((i) => ({
      id: 'gid://shopify/CartLine/' + i.key,
      originalPrice: money(i.original_price),
      priceTotal: money(i.final_line_price),
      product: { id: 'gid://shopify/Product/' + i.product_id },
      variant: { id: 'gid://shopify/ProductVariant/' + i.variant_id },
      quantity: Number(i.quantity) || 0
    }));
    const total = items.reduce((s, i) => s + (Number(i.final_line_price) || 0), 0);
    return {
      cart: { lineItems, priceTotal: money(total) },
      customer: { id: c.customerId || '', country: c.country || '' }
    };
  };

  /**
   * What is taken from a quote: the fee Redo intends (cents, with its
   * currency) and the variant Redo names as a fallback. Anything malformed →
   * null, and the caller adds nothing.
   */
  CP.parseQuote = function parseQuote(json) {
    const cp = json && Array.isArray(json.coverageProducts) && json.coverageProducts[0];
    const info = cp && cp.cartInfoToEnable;
    const variantId = info && parseInt(info.variantId, 10);
    if (!variantId || isNaN(variantId)) return null;
    const amount = cp.price && Number(cp.price.amount);
    return {
      variantId,
      productId: info.productId ? parseInt(info.productId, 10) : null,
      attribute: info.cartAttribute || CP.ATTRIBUTE,
      priceCents: amount > 0 ? Math.round(amount * 100) : null,
      currency: (cp.price && cp.price.currency) || null
    };
  };

  /**
   * The tier to put in the cart for a quoted fee: the cheapest variant whose
   * live (presentment) price is at or above the fee — the same rule Redo's
   * checkout extension applies, so cart and checkout show the same price.
   * A fee above the top tier gets the top tier. No variants → null.
   *
   * @param {Array<{id:number|string, price:number|string}>} variants - from
   *   /products/<handle>.js?currency=…; `price` in cents.
   * @param {number} feeCents
   */
  CP.pickVariantByPrice = function pickVariantByPrice(variants, feeCents) {
    const fee = Number(feeCents);
    const list = (variants || [])
      .map((v) => ({ id: Number(v.id), price: Number(v.price) }))
      .filter((v) => v.id && v.price >= 0)
      .sort((a, b) => a.price - b.price);
    if (!list.length || !(fee > 0)) return null;
    return list.find((v) => v.price >= fee) || list[list.length - 1];
  };

  /**
   * Reconcile the cart with what is desired. Returns the /cart/update.js
   * payload to send, or null when nothing needs to change.
   *
   * @param {Object} cart            /cart.js
   * @param {boolean} wanted         CP.desired(...)
   * @param {number|null} variantId  from the quote; null when unavailable
   * @param {boolean} [enabled=true] the theme feature switch. When off, only
   *   the empty-cart cleanup runs: Redo's own checkout behaviour is left alone.
   */
  CP.plan = function plan(cart, wanted, variantId, enabled) {
    const lines = CP.protectionLines(cart);
    const attrs = (cart && cart.attributes) || {};
    const updates = {};
    const attributes = {};
    let touched = false;

    if (CP.realItemCount(cart) === 0) {
      // Housekeeping only. Never records a choice.
      lines.forEach((l) => { updates[l.key] = 0; touched = true; });
      return touched ? { updates } : null;
    }

    if (enabled === false) return null;

    if (!wanted) {
      lines.forEach((l) => { updates[l.key] = 0; touched = true; });
      if (attrs[CP.ATTRIBUTE] !== 'false') { attributes[CP.ATTRIBUTE] = 'false'; touched = true; }
      return touched ? { updates, attributes } : null;
    }

    // Wanted. Tell checkout even when we cannot add a line ourselves.
    if (attrs[CP.ATTRIBUTE] !== 'true') { attributes[CP.ATTRIBUTE] = 'true'; touched = true; }
    if (variantId) {
      const keep = lines.find((l) => Number(l.variant_id) === Number(variantId));
      lines.forEach((l) => { if (l !== keep) { updates[l.key] = 0; touched = true; } });
      if (!keep) { updates[String(variantId)] = 1; touched = true; }
    } else if (lines.length > 1) {
      // No quote: keep the newest (cart.js lists newest first), drop extras.
      lines.slice(1).forEach((l) => { updates[l.key] = 0; touched = true; });
    }
    return touched ? { updates, attributes } : null;
  };

  // ---------------------------------------------------------------------------
  // Runtime — everything below needs a DOM.
  // ---------------------------------------------------------------------------

  if (typeof document === 'undefined') return;

  const config = () => theme.settings.shippingProtection || {};
  const storage = (() => { try { return root.localStorage; } catch (e) { return null; } })();

  const MAX_ATTEMPTS = 3;
  let attempts = 0;
  let inFlight = false;
  let queued = false;
  let quoteCache = { signature: null, variantId: null, price: null };
  // `pending` = a cart update is in flight; the switch shows the target
  // state (optimistically) and a busy style until the server render lands.
  let lastState = { on: false, price: null, pending: false, available: false, signature: null };

  function emitState(next) {
    lastState = Object.assign({}, lastState, next);
    document.dispatchEvent(new CustomEvent('on:cart-protection:state', { bubbles: true, detail: lastState }));
  }
  CP.getState = () => lastState;

  function refreshCart() {
    document.dispatchEvent(new CustomEvent('on:cart:change', { bubbles: true, cancelable: false }));
  }

  /**
   * Presentment price of a variant of the protection product, in cents, from
   * the public product JSON (cached per currency). Used only for the label
   * while the line is not yet in the cart; once it is, Liquid renders the
   * cart line's own price.
   */
  const productCache = {};
  const activeCurrency = () => config().currency || (root.Shopify && root.Shopify.currency && root.Shopify.currency.active) || '';
  function productVariants() {
    const cfg = config();
    const cur = activeCurrency();
    if (!cfg.productHandle) return Promise.resolve([]);
    if (!productCache[cur]) {
      productCache[cur] = fetch('/products/' + cfg.productHandle + '.js' + (cur ? '?currency=' + encodeURIComponent(cur) : ''))
        .then((r) => (r.ok ? r.json() : null))
        .then((p) => (p && Array.isArray(p.variants) ? p.variants : []))
        .catch(() => []);
    }
    return productCache[cur];
  }
  function variantPrice(variantId) {
    return productVariants().then((variants) => {
      const v = variants.find((x) => Number(x.id) === Number(variantId));
      return v ? Number(v.price) : null;
    });
  }

  function quote(cart) {
    const cfg = config();
    const signature = CP.quoteSignature(cart);
    if (quoteCache.signature === signature) return Promise.resolve(quoteCache);
    if (!cfg.storeId) return Promise.resolve({ signature, variantId: null, price: null });
    const body = CP.quoteRequest(cart, {
      currency: cfg.currency,
      country: cfg.country || (root.Shopify && root.Shopify.country) || '',
      customerId: cfg.customerId || ''
    });
    // The tier list does not depend on the quote: fetch both at once (and
    // it is usually already cached by the prefetch at load).
    const variantsReady = productVariants();
    return fetch(CP.API_HOST + '/stores/' + encodeURIComponent(cfg.storeId) + '/coverage-products', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((json) => CP.parseQuote(json))
      .then((q) => {
        if (!q) return { signature, variantId: null, price: null };
        return variantsReady.then((variants) => {
          // Tier by Redo's fee against live prices (see the file header).
          // Only when the fee is in the cart's currency; otherwise fall back
          // to the variant Redo named.
          const picked = (q.priceCents && q.currency === activeCurrency())
            ? CP.pickVariantByPrice(variants, q.priceCents)
            : null;
          if (picked) return { signature, variantId: picked.id, price: picked.price };
          return variantPrice(q.variantId).then((price) => ({ signature, variantId: q.variantId, price }));
        });
      })
      .catch(() => ({ signature, variantId: null, price: null }))
      .then((result) => { quoteCache = result; return result; });
  }

  /**
   * Bring the cart in line with the desired state. Idempotent: the update it
   * sends triggers one more merge, and that pass plans nothing.
   */
  function reconcile() {
    if (inFlight) { queued = true; return Promise.resolve(); }
    if (attempts >= MAX_ATTEMPTS) return Promise.resolve();
    inFlight = true;
    const cfg = config();

    return fetch('/cart.js')
      .then((r) => (r.ok ? r.json() : null))
      .then((cart) => {
        if (!cart) return null;
        const realCount = CP.realItemCount(cart);
        const choice = CP.readChoice(storage);
        const wanted = CP.desired({ enabled: !!cfg.enabled, defaultOn: !!cfg.defaultOn, choice, realCount });
        const lines = CP.protectionLines(cart);

        // Quote whenever the switch can show (feature on, real items), not
        // only when wanted: the OFF label needs a price too, and the quote is
        // cached by the real-line signature, so this is one call per change.
        const withQuote = (cfg.enabled && realCount > 0) ? quote(cart) : Promise.resolve({ variantId: null, price: null });
        return withQuote.then((q) => {
          const payload = CP.plan(cart, wanted, q.variantId, !!cfg.enabled);
          // What the switch should show: the line that exists, or the one
          // about to be added. Never `wanted` alone — if the quote failed
          // there is nothing to add and the server render will say OFF.
          const on = wanted && (lines.length > 0 || !!q.variantId);
          // If the line is about to be swapped for another tier, show the
          // incoming price, not the one leaving.
          const swapping = wanted && !!q.variantId && lines.length > 0 && Number(lines[0].variant_id) !== Number(q.variantId);
          const price = (lines.length && !swapping) ? Number(lines[0].final_price) : q.price;
          const available = !!q.variantId || lines.length > 0;
          const signature = CP.quoteSignature(cart);
          if (!payload) {
            attempts = 0;
            emitState({ on, price, pending: false, available, signature });
            return null;
          }
          attempts += 1;
          // Show the target state now; the server render confirms it.
          emitState({ on, price, pending: true, available, signature });
          return fetch('/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
          }).then(() => { refreshCart(); });
        });
      })
      .catch((error) => {
        // Never let this break the cart.
        // eslint-disable-next-line no-console
        console.warn('[Cart Protection] reconcile failed', error);
      })
      .finally(() => {
        inFlight = false;
        if (queued) { queued = false; reconcile(); }
      });
  }
  CP.reconcile = reconcile;

  /** The customer flipped the switch. Remember it, then reconcile. */
  CP.setChoice = function setChoice(on) {
    CP.writeChoice(storage, !!on, config().optoutDays);
    attempts = 0;
    emitState({ on: !!on, pending: true });
    return reconcile();
  };

  /**
   * What the switch should show before any network round-trip: the
   * merchant default unless the customer has opted out. The element only
   * exists when real items are in the cart, so realCount is taken as > 0.
   */
  CP.desiredNow = function desiredNow() {
    const cfg = config();
    return CP.desired({ enabled: !!cfg.enabled, defaultOn: !!cfg.defaultOn, choice: CP.readChoice(storage), realCount: 1 });
  };

  // Warm the tier list so the first quote does not wait on it.
  if (config().enabled) productVariants();

  // A cart change fires on:cart:change and, ~200ms later, the drawer's
  // on:cart:after-merge. Coalesce them so each change costs one /cart.js
  // read and at most one quote (collection-offer.js defers the same way).
  let scheduled = null;
  function schedule() {
    clearTimeout(scheduled);
    scheduled = setTimeout(() => { scheduled = null; reconcile(); }, 250);
  }
  document.addEventListener('on:cart:after-merge', schedule);
  document.addEventListener('on:cart:change', schedule);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => { reconcile(); });
  } else {
    reconcile();
  }

  // ---------------------------------------------------------------------------
  // <cart-protection-toggle> — rendered by snippets/cart-protection-toggle.liquid
  // inside a data-merge region, so its checked state and price come from the
  // server on every cart change. JS only fills the price while the line is
  // not in the cart yet, and handles the flip.
  // ---------------------------------------------------------------------------

  class CartProtectionToggle extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input[type="checkbox"]');
      this.priceEl = this.querySelector('[data-cp-price]');
      if (!this.input) return;
      this.onChange = (evt) => {
        const on = !!evt.target.checked;
        this.setChecked(on);
        this.setBusy(true);
        CP.setChoice(on);
      };
      this.onState = (evt) => this.render(evt.detail);
      this.input.addEventListener('change', this.onChange);
      document.addEventListener('on:cart-protection:state', this.onState);

      this.calcText = this.dataset.calculating || 'Calculating…';
      const state = CP.getState();

      // The server rendered this element for the cart as it is now
      // (data-signature). If Redo's last quote was for a different cart, the
      // price on screen belongs to the old cart and is about to change —
      // say so instead of showing a stale amount.
      if (this.dataset.price && state.signature && state.signature !== this.dataset.signature) {
        this.showCalculating();
      }

      // Optimistic first paint: the server rendered "checked" from whether a
      // "re:do" line exists, but on the first add there is none yet and the
      // add takes a few seconds. If protection is wanted, show ON and
      // "calculating" now; the reconcile that follows either confirms it or,
      // if nothing could be added, turns it back off.
      if (!this.dataset.price && !this.input.checked && CP.desiredNow()) {
        this.setChecked(true);
        this.showCalculating();
      }
      this.render(state);
    }

    setChecked(on) {
      if (!this.input) return;
      this.input.checked = !!on;
      this.input.setAttribute('aria-checked', String(!!on));
    }

    setBusy(busy) {
      this.classList.toggle('cart-protection--busy', !!busy);
    }

    showCalculating() {
      this.calculating = true;
      if (this.priceEl) this.priceEl.textContent = this.calcText;
      this.setBusy(true);
    }

    disconnectedCallback() {
      if (this.input) this.input.removeEventListener('change', this.onChange);
      document.removeEventListener('on:cart-protection:state', this.onState);
    }

    showPrice(cents) {
      if (!this.priceEl || cents == null) return;
      this.priceEl.textContent = typeof theme.formatMoney === 'function'
        ? theme.formatMoney(cents)
        : (cents / 100).toFixed(2);
    }

    render(state) {
      if (!state || !this.input) return;
      // A state is "for this cart" when its quote signature matches the
      // cart this element was rendered for. A stale state must never
      // overwrite a fresh server price.
      const forThisCart = !state.signature || !this.dataset.signature || state.signature === this.dataset.signature;

      if (state.pending) {
        // A change is in flight: show its target state and price.
        this.setChecked(state.on);
        this.setBusy(true);
        if (state.price != null) this.showPrice(state.price);
        else if (this.calculating && this.priceEl) this.priceEl.textContent = this.calcText;
        return;
      }

      if (!forThisCart) return; // reconcile for the new cart has not run yet

      this.setBusy(false);
      // Settled. The server render is the truth — except when the optimistic
      // ON could not be honoured (nothing was added): turn it back off.
      if (!this.dataset.price && this.input.checked && !state.on && !state.available) {
        this.setChecked(false);
      }
      if (state.price != null && (this.calculating || !this.dataset.price)) this.showPrice(state.price);
      else if (this.calculating && this.dataset.price) this.showPrice(Number(this.dataset.price));
      this.calculating = false;
    }
  }

  if (!root.customElements.get('cart-protection-toggle')) {
    root.customElements.define('cart-protection-toggle', CartProtectionToggle);
  }
})();
